import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import { TextBox, TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import React, { useCallback, useMemo, useState } from "react";
import { getParamValue } from "../../../commons/helpers/ObjectHelper";
import Acheteurs from "../../../components/Acheteurs";
import { Keys } from "../../../constante/keys";
import { UrlParams } from "../../../constante/UrlParams";
import useUrlParamsHandler from "../../../hooks/useUrlParamsHandler";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { Division } from "../../../models/Division";
import { User } from "../../../models/User";
import {
  planoAPI,
  useGetDivisionsQuery,
  useGetUsersQuery,
} from "../../../redux/features/cache";
import { useAppDispatch } from "../../../redux/store";
import { CustomTreeView } from "../../planogramme/components/CustomTreeView";

export interface IHeaderSectionProps {
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  edit: boolean;
  div: string;
  setDiv: any;
  user: string;
  setUser: any;
  totalProducts?: number;
}

export default function HeaderSection({
  inputValue,
  setInputValue,
  edit,
  div,
  setDiv,
  user,
  setUser,
  totalProducts,
}: IHeaderSectionProps) {
  const dispatch = useAppDispatch();
  const userPreferencesHook = useUserPreferences();
  const { data: divisions = [] as Division[] } = useGetDivisionsQuery({
    treeView: true,
    includeSections: false,
    plano: true,
    user: user,
  });
  const { data: users = [] as User[] } = useGetUsersQuery({
    fonction: "Plano",
    sort: "usr_nom",
    div_num: div,
  });
  const acheteursSelected = useMemo(() => {
    return users.filter((u) => {
      let currentUsers = user.split(",");
      return currentUsers.includes(u.usr_cd);
    });
  }, [user, users]);
  const divisionsSelected = useMemo(() => {
    return divisions.filter((e) => {
      let currentDivisions = div.split(",");
      return currentDivisions.includes(e.div_num.toString());
    });
  }, [div, divisions]);
  const urlParamsHandler = useUrlParamsHandler();

  const updateObject = useCallback(
    (
      object: any,
      currentValues: any[],
      key: string,
      update: any,
      name: string
    ) => {
      const newValue = getParamValue(currentValues, object, key);
      update(newValue);
      urlParamsHandler.setParamsToUrl(name, newValue);

      userPreferencesHook.setFilterPreferences(name, newValue);
    },
    [urlParamsHandler, userPreferencesHook]
  );

  const divisionsChildren = [
    (division: Division) =>
      updateObject(
        division,
        divisionsSelected,
        Keys.Division,
        setDiv,
        UrlParams.Division
      ),
  ];
  const [selected, setSelected] = useState<number>(0);

  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };

  const onAcheteurChange = useCallback(
    (event: MultiSelectChangeEvent) => {
      let user = event.value.map((obj: User) => obj.usr_cd).join(",");
      setUser(user);
      urlParamsHandler.setParamsToUrl(
        UrlParams.Acheteur,
        user !== undefined ? user : ""
      );
    },
    [setUser, urlParamsHandler]
  );

  const getCheckIds = useCallback((): string[] => {
    const ids: string[] = [];

    divisionsSelected.forEach((d) => {
      let value = divisions.find((a) => a["div_num"] === d["div_num"]);

      if (value) ids.push(value["div_num"].toString());
    });

    return ids;
  }, [divisions, divisionsSelected]);

  return (
    <>
      <div className="k-d-flex k-flex-column k-h-full k-gap-1">
        <TextBox
          onChange={(event: TextBoxChangeEvent) => {
            setInputValue((event.value as string).toLowerCase());
          }}
          placeholder="Recherche"
          disabled={edit}
          value={inputValue}
        />
        <TabStrip
          keepTabsMounted={true}
          selected={selected}
          onSelect={handleSelect}
          className="k-h-full k-overflow-auto"
        >
          <TabStripTab title="Sec/Acht">
            <CustomTreeView
              data={divisions}
              children={divisionsChildren}
              acheteurs={
                <Acheteurs
                  onAcheteurChange={onAcheteurChange}
                  user={acheteursSelected}
                  users={users}
                />
              }
              checkIds={getCheckIds()}
              useScroll={false}
              selectedTab={0}
              invalidateQuery={() =>
                dispatch(planoAPI.util.invalidateTags(["divisions"]))
              }
              totalProducts={totalProducts}
            />
          </TabStripTab>
        </TabStrip>
      </div>
    </>
  );
}
